import React, { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { philosophyData } from '../data/mock';

/**
 * PhilosophySection Component
 * 
 * Section "KAIROS ≠ CHRONOS" avec timeline interactive:
 * - Deux lignes SVG côte à côte (linéaire vs courbe)
 * - Animation au scroll ou au clic
 * - Point animé qui avance sur la ligne Kairos
 * - Texte qui apparaît de façon dynamique
 */
const PhilosophySection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: false, margin: '-20%' });
  const [activePoint, setActivePoint] = useState(0);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  
  // Transform scroll progress to timeline progress
  const timelineProgress = useTransform(scrollYProgress, [0.2, 0.8], [0, 1]);
  
  return (
    <section
      id="philosophie"
      ref={sectionRef}
      className="section-container bg-[#0A0A0A] overflow-hidden"
    >
      {/* Section Header */}
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
      >
        <p className="text-[#2A3B8F] uppercase tracking-widest text-sm mb-4">
          Notre différence
        </p>
        <h2 className="heading-lg mb-6">
          KAIROS <span className="text-[#555555]">≠</span> CHRONOS
        </h2>
        <p className="body-md text-[#888888] max-w-2xl mx-auto">
          Dans la philosophie grecque antique, le temps a deux visages. 
          Nous avons choisi le nôtre.
        </p>
      </motion.div>
      
      {/* Interactive Timelines */}
      <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
        {/* CHRONOS Timeline (Linear) */}
        <motion.div
          className="relative p-8 lg:p-12 border border-white/5 bg-[#1A1A1A]/50"
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          {/* Top accent line */}
          <motion.div
            className="absolute top-0 left-0 h-1 bg-[#555555]"
            initial={{ width: 0 }}
            animate={isInView ? { width: '100%' } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
          
          <span className="text-[#555555] text-xs uppercase tracking-widest">
            {philosophyData.chronos.subtitle}
          </span>
          
          <h3 className="heading-md text-[#888888] mt-4 mb-2">
            {philosophyData.chronos.title}
          </h3>
          
          <p className="text-[#555555] mb-8">
            {philosophyData.chronos.description}
          </p>
          
          {/* Linear Timeline SVG */}
          <div className="relative h-16 mb-8">
            <svg className="w-full h-full" viewBox="0 0 400 60">
              {/* Base line */}
              <line x1="20" y1="30" x2="380" y2="30" stroke="#333" strokeWidth="2" />
              
              {/* Tick marks - evenly spaced */}
              {[...Array(8)].map((_, i) => (
                <g key={i}>
                  <line
                    x1={20 + i * 51.4}
                    y1="20"
                    x2={20 + i * 51.4}
                    y2="40"
                    stroke="#555"
                    strokeWidth="2"
                  />
                  <motion.circle
                    cx={20 + i * 51.4}
                    cy="30"
                    r="4"
                    fill="#555"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.5 + i * 0.1 }}
                  />
                </g>
              ))}
            </svg>
          </div>
          
          {/* Points list */}
          <ul className="space-y-4">
            {philosophyData.chronos.points.map((point, index) => (
              <motion.li
                key={index}
                className="flex justify-between items-start border-b border-white/5 pb-4"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <span className="text-[#555555] text-sm">{point.label}</span>
                <span className="text-[#888888] text-sm text-right max-w-[60%]">{point.value}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
        
        {/* KAIROS Timeline (Curved with key points) */}
        <motion.div
          className="relative p-8 lg:p-12 border border-[#2A3B8F]/30 bg-[#1A1A1A]"
          initial={{ opacity: 0, x: 50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          {/* Top accent line */}
          <motion.div
            className="absolute top-0 left-0 h-1 bg-[#2A3B8F]"
            initial={{ width: 0 }}
            animate={isInView ? { width: '100%' } : {}}
            transition={{ duration: 0.8, delay: 0.5 }}
          />
          
          <span className="text-[#2A3B8F] text-xs uppercase tracking-widest">
            {philosophyData.kairos.subtitle}
          </span>
          
          <h3 className="heading-md text-white mt-4 mb-2">
            {philosophyData.kairos.title}
          </h3>
          
          <p className="text-[#888888] mb-8">
            {philosophyData.kairos.description}
          </p>
          
          {/* Curved Timeline SVG - Interactive */}
          <div className="relative h-20 mb-8">
            <svg className="w-full h-full" viewBox="0 0 400 70">
              {/* Curved path */}
              <motion.path
                d="M 20 50 Q 100 10, 200 35 T 380 20"
                fill="none"
                stroke="#2A3B8F"
                strokeWidth="2"
                initial={{ pathLength: 0 }}
                animate={isInView ? { pathLength: 1 } : {}}
                transition={{ duration: 1.5, delay: 0.4 }}
              />
              
              {/* Key inflection points - clickable */}
              {[{ cx: 100, cy: 18, label: 'Découverte' }, { cx: 200, cy: 35, label: 'Inflexion' }, { cx: 320, cy: 22, label: 'Opportunité' }].map((point, i) => (
                <g key={i}>
                  {/* Glow effect */}
                  <motion.circle
                    cx={point.cx}
                    cy={point.cy}
                    r={activePoint === i ? 16 : 10}
                    fill="rgba(42, 59, 143, 0.2)"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.8 + i * 0.2 }}
                  />
                  {/* Main point */}
                  <motion.circle
                    cx={point.cx}
                    cy={point.cy}
                    r="8"
                    fill="#2A3B8F"
                    className="cursor-pointer"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.8 + i * 0.2, type: 'spring' }}
                    whileHover={{ scale: 1.3 }}
                    onClick={() => setActivePoint(i)}
                  />
                </g>
              ))}
              
              {/* Animated traveling point */}
              <motion.circle
                cx="20"
                cy="50"
                r="5"
                fill="#fff"
                style={{ filter: 'drop-shadow(0 0 6px rgba(42, 59, 143, 0.8))' }}
              >
                <animateMotion
                  dur="4s"
                  repeatCount="indefinite"
                  path="M 0 0 Q 80 -40, 180 -15 T 360 -30"
                />
              </motion.circle>
            </svg>
          </div>
          
          {/* Points list */}
          <ul className="space-y-4">
            {philosophyData.kairos.points.map((point, index) => (
              <motion.li
                key={index}
                className="flex justify-between items-start border-b border-[#2A3B8F]/20 pb-4"
                initial={{ opacity: 0, x: 20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: 0.7 + index * 0.1 }}
              >
                <span className="text-[#2A3B8F] text-sm">{point.label}</span>
                <span className="text-white text-sm text-right max-w-[60%]">{point.value}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

export default PhilosophySection;
